import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CreditCard, Search, ChevronDown, ChevronRight, RefreshCw, XCircle, Bell, ArrowRightLeft, Users } from "lucide-react";
import { toast } from "sonner";
import { apiCall } from "@/components/admin/people/peopleUtils";

const planColors = {
  free_trial: "bg-slate-100 text-slate-700",
  starter: "bg-blue-100 text-blue-700",
  plus: "bg-indigo-100 text-indigo-700",
  pro: "bg-purple-100 text-purple-700",
  school_starter: "bg-emerald-100 text-emerald-700",
  school_plus: "bg-teal-100 text-teal-700",
  school_pro: "bg-cyan-100 text-cyan-700",
};

export default function AdminSubscriptions() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("all");
  const [status, setStatus] = useState("all");
  const [plan, setPlan] = useState("all");
  const [expanded, setExpanded] = useState(new Set());
  const [modal, setModal] = useState(null); // {child, parent}
  const [newPlan, setNewPlan] = useState("");
  const [renewDays, setRenewDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search) qs.append("search", search);
      if (source !== "all") qs.append("source", source);
      if (status !== "all") qs.append("status", status);
      if (plan !== "all") qs.append("plan", plan);
      const d = await apiCall(`/admin/auth/subscribers/master?${qs.toString()}`);
      setRows(d.subscribers || []);
      setStats(d.stats || {});
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [search, source, status, plan]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { apiCall("/admin/subscriptions/plans").then((d) => setPlans(d.plans || [])).catch(() => {}); }, []);

  const toggle = (id) => {
    const n = new Set(expanded);
    n.has(id) ? n.delete(id) : n.add(id);
    setExpanded(n);
  };

  const openManage = (child, parent) => { setNewPlan(child.subscription?.plan_type || ""); setRenewDays(30); setModal({ child, parent }); };

  const runAction = async (action) => {
    const child = modal.child;
    setBusy(true);
    try {
      if (action === "change-plan") {
        if (!newPlan) { toast.error("Pick a plan"); setBusy(false); return; }
        await apiCall(`/admin/subscriptions/${child.id}/change-plan`, { method: "POST", body: { plan_type: newPlan } });
        toast.success("Plan changed");
      } else if (action === "renew") {
        await apiCall(`/admin/subscriptions/${child.id}/renew`, { method: "POST", body: { days: Number(renewDays) || 30 } });
        toast.success(`Extended by ${renewDays} days`);
      } else if (action === "cancel") {
        if (!window.confirm(`Cancel subscription for ${child.name}?`)) { setBusy(false); return; }
        await apiCall(`/admin/subscriptions/${child.id}/cancel`, { method: "POST" });
        toast.success("Subscription cancelled");
      } else if (action === "remind") {
        const d = await apiCall(`/admin/subscriptions/${child.id}/send-reminder`, { method: "POST" });
        toast.success(d.message || "Reminder sent");
      }
      setModal(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const planBadge = (p) => <Badge className={planColors[p] || "bg-slate-100 text-slate-700"}>{(p || "free_trial").replace(/_/g, " ").toUpperCase()}</Badge>;
  const statusBadge = (s) => <Badge className={s === "active" ? "bg-emerald-100 text-emerald-700" : s === "cancelled" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>{s || "—"}</Badge>;

  const StatCard = ({ label, value, color }) => (
    <div className={`rounded-xl p-4 border ${color}`}><p className="text-xs opacity-80">{label}</p><p className="text-2xl font-bold">{value ?? 0}</p></div>
  );

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><CreditCard className="h-6 w-6 text-indigo-600" /> Subscriptions</h1>
        <p className="text-sm text-slate-500">Manage B2C and B2B subscriptions — change plan, renew, cancel, remind</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Parents" value={stats.total_parents} color="bg-slate-50 border-slate-200 text-slate-700" />
        <StatCard label="Children" value={stats.total_children} color="bg-indigo-50 border-indigo-200 text-indigo-700" />
        <StatCard label="B2C" value={stats.b2c_count} color="bg-blue-50 border-blue-200 text-blue-700" />
        <StatCard label="B2B (School)" value={stats.b2b_count} color="bg-purple-50 border-purple-200 text-purple-700" />
        <StatCard label="Active" value={stats.active_count} color="bg-emerald-50 border-emerald-200 text-emerald-700" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by parent name / email / phone" className="pl-10" data-testid="subs-search" />
        </div>
        <select className="border border-slate-200 rounded-md p-2 text-sm" value={source} onChange={(e) => setSource(e.target.value)} data-testid="subs-source-filter">
          <option value="all">All sources</option><option value="b2c">B2C (Direct)</option><option value="b2b">B2B (School)</option>
        </select>
        <select className="border border-slate-200 rounded-md p-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="subs-status-filter">
          <option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option>
        </select>
        <select className="border border-slate-200 rounded-md p-2 text-sm" value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="subs-plan-filter">
          <option value="all">All plans</option>
          {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr><th className="p-4 w-8"></th><th className="p-4">Parent</th><th className="p-4">Email</th><th className="p-4">Type</th><th className="p-4">Children</th><th className="p-4">Status</th></tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan={6} className="p-8 text-center text-slate-400">Loading…</td></tr>)
              : rows.length === 0 ? (<tr><td colSpan={6} className="p-8 text-center text-slate-400">No subscribers found</td></tr>)
              : rows.map((r) => (
                <>
                  <tr key={r.id} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => toggle(r.id)} data-testid={`sub-row-${r.id}`}>
                    <td className="p-4">{expanded.has(r.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                    <td className="p-4 font-medium text-slate-800">{r.parent_name || "—"}</td>
                    <td className="p-4 text-slate-600">{r.parent_email}</td>
                    <td className="p-4"><Badge className={r.subscriber_type === "b2b" ? "bg-purple-100 text-purple-700" : r.subscriber_type === "mixed" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}>{r.subscriber_type?.toUpperCase()}</Badge></td>
                    <td className="p-4 text-slate-600"><span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{r.children_count}</span></td>
                    <td className="p-4">{statusBadge(r.status)}</td>
                  </tr>
                  {expanded.has(r.id) && (r.children || []).map((c) => (
                    <tr key={c.id} className="bg-slate-50/60 border-t border-slate-100" data-testid={`sub-child-${c.id}`}>
                      <td></td>
                      <td className="p-3 pl-8 text-slate-700">{c.name} {c.grade ? <span className="text-xs text-slate-400">· Grade {c.grade}</span> : null}</td>
                      <td className="p-3 text-slate-500 text-xs">{c.school_name || c.student_id || "—"}</td>
                      <td className="p-3">{planBadge(c.subscription?.plan_type)}</td>
                      <td className="p-3">{statusBadge(c.subscription?.status)}</td>
                      <td className="p-3">
                        <Button size="sm" variant="outline" onClick={() => openManage(c, r)} data-testid={`sub-manage-${c.id}`}>Manage</Button>
                      </td>
                    </tr>
                  ))}
                </>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="sub-manage-modal">
          <DialogHeader><DialogTitle>Manage subscription · {modal?.child?.name}</DialogTitle></DialogHeader>
          {modal && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                Current: {planBadge(modal.child.subscription?.plan_type)} {statusBadge(modal.child.subscription?.status)}
                {modal.child.subscription?.end_date && <span className="text-xs text-slate-400">· ends {new Date(modal.child.subscription.end_date).toLocaleDateString()}</span>}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-slate-500">Change plan</label>
                <div className="flex gap-2">
                  <select className="flex-1 border border-slate-200 rounded-md p-2 text-sm" value={newPlan} onChange={(e) => setNewPlan(e.target.value)} data-testid="sub-plan-select">
                    <option value="">Select plan…</option>
                    {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <Button onClick={() => runAction("change-plan")} disabled={busy} data-testid="sub-changeplan-btn"><ArrowRightLeft className="h-4 w-4 mr-1" /> Apply</Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-slate-500">Renew / Extend</label>
                <div className="flex gap-2 items-center">
                  <Input type="number" min={1} value={renewDays} onChange={(e) => setRenewDays(e.target.value)} className="w-28" data-testid="sub-renew-days" />
                  <span className="text-sm text-slate-500">days</span>
                  <Button onClick={() => runAction("renew")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sub-renew-btn"><RefreshCw className="h-4 w-4 mr-1" /> Extend</Button>
                </div>
              </div>

              <div className="flex gap-2 pt-2 border-t border-slate-100">
                <Button variant="outline" onClick={() => runAction("remind")} disabled={busy} data-testid="sub-remind-btn"><Bell className="h-4 w-4 mr-1" /> Send reminder</Button>
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => runAction("cancel")} disabled={busy} data-testid="sub-cancel-btn"><XCircle className="h-4 w-4 mr-1" /> Cancel</Button>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setModal(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
